import type { FC, ReactNode } from 'react';
import React from 'react';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';

import type { EmojiDescription } from '@atlaskit/emoji/types';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import Inline from '@atlaskit/primitives/inline';
import Pressable from '@atlaskit/primitives/pressable';
import TeamsIcon from '@atlaskit/icon/core/teams';

import { ConfluenceSpaceAri } from '@atlassian/ari';
import { ConfluenceTeamButton } from '@atlassian/teams/confluence-team-button';

import { DocumentSpaceTitle } from '@confluence/document-title/entry-points/DocumentSpaceTitle.ts';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import {
	EMOJI_SIZE_MEDIUM,
	EMOJI_SIZE_LARGE,
} from '@confluence/emoji-title/entry-points/emojiTitleConstants.ts';
import {
	Breadcrumbs,
	FixedSpaceHeaderContainer,
} from '@confluence/breadcrumbs/entry-points/Breadcrumbs.tsx';
import { ContentClassificationTag } from '@confluence/data-classification/entry-points/ContentClassificationTag.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import {
	useContentClassification,
	useDefaultAndSpaceClassificationLevels,
} from '@confluence/data-classification/entry-points/queries.ts';
import { COVER_IMAGE_FIXED_WIDTH } from '@confluence/page-cover-picture/entry-points/pageCoverPictureConstants.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { isPersonalSpace as checkIfPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceTitleContainer = styled.div<{
	contentAppearance: any;
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ contentAppearance, isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		contentAppearance !== ContentAppearanceType.DEFAULT
			? isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji
				? { display: 'flex' }
				: {}
			: {
					margin: `0 auto ${token('space.300')}`,
					wordBreak: 'break-word',
					wordWrap: 'break-word',
					maxWidth: '760px',
				},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SpaceHeaderText: React.ComponentType<
	{
		isTitleCenterAligned?: boolean;
		hasCoverPicture?: boolean;
		hasIcon?: boolean;
	} & React.ClassAttributes<HTMLHeadingElement> &
		React.HTMLAttributes<HTMLHeadingElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.h1<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	hasIcon?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ hasCoverPicture, isTitleCenterAligned, hasIcon }) =>
		hasIcon && !isTitleCenterAligned && !hasCoverPicture ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, hasIcon }) =>
		hasCoverPicture && hasIcon ? '-40px !important' : '72px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'flex' : 'inline-flex'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	flexDirection: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned || hasCoverPicture ? 'column' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'intial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textAlign: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	flex: 1,
	wordBreak: 'break-word',
	wordWrap: 'break-word',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? 'initial' : token('space.250'),
	font: token('font.heading.xlarge'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ hasCoverPicture, isTitleCenterAligned }) => ({
		gap: hasCoverPicture
			? token('space.600')
			: isTitleCenterAligned // NOSONAR - SonarQube complains about nested ternary operator, but fixing it might break the dynamic styling in Compiled (known bug there)
				? token('space.200')
				: token('space.150'),
	}),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.span({
	display: 'inline-flex',
	flex: 0,
	marginRight: token('space.075'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingBottom: '1px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapperWithAlignment = styled(EmojiWrapper)({
	marginRight: token('space.0'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconStyleWrapper = styled.span<{
	isTitleCenterAligned?: boolean;
	isFixedWidthImageOption?: boolean;
	isPageContentFullWidth?: boolean;
	hasCoverPicture?: boolean;
	isCompanyHub?: boolean;
}>({
	display: 'flex',
	alignItems: 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'center' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isFixedWidthImageOption, hasCoverPicture, isTitleCenterAligned }) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${COVER_IMAGE_FIXED_WIDTH}px`
			: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: ({ isFixedWidthImageOption, hasCoverPicture, isTitleCenterAligned }) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned ? '0 auto' : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ isCompanyHub, hasCoverPicture }) =>
		!hasCoverPicture && isCompanyHub ? token('space.150') : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({
		isFixedWidthImageOption,
		hasCoverPicture,
		isTitleCenterAligned,
		isPageContentFullWidth,
	}) =>
		isFixedWidthImageOption && hasCoverPicture && !isTitleCenterAligned && isPageContentFullWidth
			? token('space.800')
			: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceIcon = styled.img({
	borderRadius: token('radius.large'),
});

const BREADCRUMBS_MIN_WIDTH = '150px';

const breadcrumbsWrapper = xcss({
	minWidth: BREADCRUMBS_MIN_WIDTH, // needed for responsive breadcrumbs
	display: 'flex',
	flexWrap: 'nowrap',
	gap: 'space.050',
	alignItems: 'center',
});

const titleInlineStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	gap: 'space.100',
});

const titleCenterAlignedInlineStyles = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 'space.100',
});

const buttonStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	display: 'flex',
	alignItems: 'center',
	height: '32px',
	width: '32px',
	justifyContent: 'center',
	color: 'color.text.subtle',
	transition: token('motion.button.hovered'),
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
		transition: token('motion.button.pressed'),
	},
});
const TeamsButtonPlaceholder = () => (
	<Pressable xcss={[buttonStyles]} testId="team-button-ssr-placeholder" isDisabled>
		<TeamsIcon label="team-icon-placeholder" />
	</Pressable>
);

interface SpaceHeaderProps {
	children?: ReactNode;
	spaceKey: string;
	isThemed?: boolean;
	spaceIconPath?: string;
	contentId?: string;
}

type SpaceHeaderTextProps = {
	title: ReactNode | string;
	emoji: string | EmojiDescription | null;
	isTitleCenterAligned?: boolean;
	contentAppearance?: string;
	hasCoverPicture?: boolean;
	spaceIconPath?: string;
	isCompanyHub?: boolean;
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji?: boolean;
	isPageContentFullWidth?: boolean;
	isFixedWidthImageOption?: boolean;
	cloudId?: string;
	spaceId?: string;
	spaceKey?: string;
	orgId?: string;
};

export const SpaceHeading = ({
	emoji,
	title,
	isTitleCenterAligned,
	isCompanyHub,
	contentAppearance,
	hasCoverPicture,
	spaceIconPath,
	isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji,
	isPageContentFullWidth,
	isFixedWidthImageOption,
	cloudId,
	spaceId,
	spaceKey,
	orgId,
}: SpaceHeaderTextProps): React.JSX.Element => {
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const emojiHeight = hasCoverPicture ? EMOJI_SIZE_LARGE : EMOJI_SIZE_MEDIUM;

	const hasEmoji = Boolean(emoji);

	const isPersonalSpace = checkIfPersonalSpace(spaceKey);

	const renderSpaceTitleAndTeamButton = () => {
		if (renderServerPlaceholder) {
			return (
				<Inline xcss={isTitleCenterAligned ? titleCenterAlignedInlineStyles : titleInlineStyles}>
					{title}
					<TeamsButtonPlaceholder />
				</Inline>
			);
		} else {
			return (
				<Inline xcss={isTitleCenterAligned ? titleCenterAlignedInlineStyles : titleInlineStyles}>
					{title}
					{cloudId && spaceId && !isCompanyHub && !isPersonalSpace && (
						<ConfluenceTeamButton
							containerId={ConfluenceSpaceAri.create({
								siteId: cloudId,
								spaceId,
							}).toString()}
							// This prop is used for presenation purposes only, 1 renders the team button content as an icon, matching the SSR placeholder
							numberOfTeams={1}
							orgId={orgId}
						/>
					)}
				</Inline>
			);
		}
	};

	return (
		<SpaceTitleContainer
			contentAppearance={contentAppearance}
			isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji={
				isLeftAlignedTitleFullWidthPageWithFixedWidthImageAndEmoji
			}
		>
			<SpaceHeaderText
				isTitleCenterAligned={isTitleCenterAligned}
				hasCoverPicture={hasCoverPicture}
				hasIcon={hasEmoji || !!spaceIconPath}
				data-test-id="title-text"
				id="title-text"
			>
				{hasEmoji || !!spaceIconPath ? (
					<IconStyleWrapper
						isTitleCenterAligned={isTitleCenterAligned}
						isFixedWidthImageOption={isFixedWidthImageOption}
						isPageContentFullWidth={isPageContentFullWidth}
						isCompanyHub={isCompanyHub}
						hasCoverPicture={hasCoverPicture}
						data-testid="emoji"
					>
						{hasEmoji ? (
							<LazyEmojiComponentLoader
								emoji={emoji}
								context="spaceHeader"
								// TODO: update this to use size constants
								height={emojiHeight}
								wrapper={EmojiWrapperWithAlignment}
								renderResourcedEmoji
								isPageTitleComponent
							/>
						) : (
							<SpaceIcon
								data-vc="space-header-icon"
								width={emojiHeight}
								height={emojiHeight}
								src={`${CONTEXT_PATH}${spaceIconPath}`}
								alt=""
							/>
						)}
					</IconStyleWrapper>
				) : null}
				{renderSpaceTitleAndTeamButton()}
			</SpaceHeaderText>
		</SpaceTitleContainer>
	);
};

export const SpaceHeader: FC<SpaceHeaderProps> = ({ children, spaceKey, isThemed, contentId }) => {
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});

	const spaceClassificationMetadata = useDefaultAndSpaceClassificationLevels({ spaceKey });

	return (
		<FixedSpaceHeaderContainer isThemed={isThemed}>
			<DocumentSpaceTitle spaceKey={spaceKey} />
			<Box xcss={breadcrumbsWrapper} testId="breadcrumbs-and-classification-tag">
				<Breadcrumbs spaceKey={spaceKey} />
				<div data-testid="content-classification-tag">
					<ContentClassificationTag
						contentId={contentId ?? ''}
						contentType="page"
						contentClassificationMetadata={contentClassificationMetadata}
						spaceClassificationMetadata={spaceClassificationMetadata}
						isEditMode={false}
					/>
				</div>
			</Box>

			{children}
		</FixedSpaceHeaderContainer>
	);
};
